/**
 * Shared SEO helpers for JSON-LD structured data.
 * Used by hub pages (/blog, /glossary, /compare, /topics, /strategy-score, /roast)
 * to emit BreadcrumbList schema that Google explicitly recommends for
 * content-hub indexing.
 */
import { useEffect } from 'react';

const SITE_URL = 'https://isupplyai.com';

/**
 * useBreadcrumbSchema — injects a BreadcrumbList JSON-LD script into <head>
 * and removes it on unmount. Pass the breadcrumb trail starting from "Home".
 *
 * @param {string} idSuffix - Unique tag identifier (e.g. "blog-index").
 * @param {Array<{name: string, path: string}>} trail - Ordered list of crumbs.
 *   The last crumb is the current page. Paths must be absolute (e.g. "/blog").
 */
export const useBreadcrumbSchema = (idSuffix, trail) => {
  useEffect(() => {
    if (!trail || trail.length === 0) return undefined;

    const itemListElement = trail.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.path}`,
    }));

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement,
    };

    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = `breadcrumb-schema-${idSuffix}`;
    el.textContent = JSON.stringify(schema);
    document.head.appendChild(el);

    return () => {
      el.remove();
    };
  }, [idSuffix, trail]);
};

export { SITE_URL };
